import {Button, Form, Modal} from "react-bootstrap";
import {useParams} from "react-router-dom";
import React, {useMemo} from "react";
import {Field, Formik} from "formik";
import {FormikControl, FormRowGroup, Oracle} from "../Components";
import CronEditor from "../cron/CronEditor";
import API, {DataAPI, MutationAPI, useData, useMutator} from "../../services/API";
import {Validation} from "../../commons/Utils";
import type { ReportDistributionInfo} from "../../services/API";
import {useHandleClose, ModalUrl} from "../../commons/ModalHelpers";
import {UseMutationResult, useQueryClient} from "react-query";
import Notifications from "../Notifications";
import Reports from "../Reports";
import {useReports} from "../../hooks";
import {
    DayParamValues,
    RangeParamValues,
    getLocationIdValue,
    getPrinterIdValue,
    hasSelectableValue,
    parseDistributionParams,
    toLocationOption,
    toPrinterOption,
} from "../../commons/DistributionUtils";
import {DEFAULT_REPORT_CRON, validateQuartzCronForMinuteScheduler} from "../../commons/CronUtils";

const FormikOracleField = ({name, onSearch, onValueChange, ...props}) => (
    <Field name={name}>
        {({ field, form, meta }) => {
            const value = field.value || null;
            const optionId = value && typeof value === 'object' ? value.id : value;
            const optionLabel = value && typeof value === 'object' ? value.label : '';
            const handleChange = (selected) => {
                const nextValue = selected || null;
                form.setFieldValue(field.name, nextValue);
                form.setFieldTouched(field.name, true, false);
                if (typeof onValueChange === 'function') {
                    onValueChange(nextValue, form);
                }
            }

            return <>
                <Oracle
                    key={`${name}-${optionId || 'empty'}-${optionLabel || ''}`}
                    {...field}
                    {...props}
                    value={value}
                    onSearch={onSearch}
                    onChange={handleChange}
                />
                {(meta.touched && meta.error) ?
                    <div className="feedback-invalid">{meta.error}</div>
                    : null}
            </>
        }}
    </Field>
)

const getDefaultParams = (reportId) => {
    switch (reportId) {
        case 'counters_day':
        case 'counters_range':
        case 'consumables_range':
            return {
                range: 0,
                locationId: null,
                printerId: null,
            };
        default:
            return {};
    }
}

const normalizeDistributionParams = (reportId, params, location = null, printer = null) => {
    const normalized = {
        ...getDefaultParams(reportId),
        ...params,
    };
    if (location) {
        normalized.locationId = toLocationOption(location);
    } else if (normalized.locationId) {
        normalized.locationId = toLocationOption(normalized.locationId);
    }
    if (printer) {
        normalized.printerId = toPrinterOption(printer);
    } else if (normalized.printerId) {
        normalized.printerId = toPrinterOption(normalized.printerId);
    }
    return normalized;
}

const serializeDistributionParams = (reportId, params) => {
    const normalized = normalizeDistributionParams(reportId, params);
    return JSON.stringify({
        ...normalized,
        locationId: getLocationIdValue(normalized.locationId),
        printerId: getPrinterIdValue(normalized.printerId),
    });
}

const AddEditDistribution = () => {

    const { id } = useParams();
    const isNew = id === 'new';

    let reports = useReports();
    const queryClient = useQueryClient();

    const handleClose = useHandleClose(Reports.url);
    const mutateDistribution: UseMutationResult<ReportDistributionInfo> = useMutator(MutationAPI.addEditDistribution, {
        afterSuccess: () => {
            queryClient.invalidateQueries('reportdistribution');
            if (!isNew) {
                queryClient.invalidateQueries(['distribution', id]);
            }
            handleClose();
        },
        customMsg: 'Dystrybucja została ' + (isNew ? 'dodana' : 'zaktualizowana')
    });
    const { isLoading, error, data } = useData(id, 'distribution', DataAPI.getDistribution, {enabled: !isNew});
    const parsedDistributionParams = useMemo(() => parseDistributionParams(data ? data.params : null), [data]);
    const distributionLocationId = getLocationIdValue(parsedDistributionParams.locationId);
    const distributionPrinterId = getPrinterIdValue(parsedDistributionParams.printerId);
    const { data: distributionLocation } = useData(distributionLocationId, 'location', DataAPI.getLocation, {
        enabled: !isNew && !!distributionLocationId,
    });
    const { data: distributionPrinter } = useData(distributionPrinterId, 'printer', DataAPI.getPrinterDetails, {
        enabled: !isNew && !!distributionPrinterId,
    });

    const getLocations = (filter) => {
        return API.getLocations(filter).then(res => res.data);
    }

    const getPrinters = (filter) => {
        return API.getPrintersByQuery(filter).then(res => (res.data || []).map(toPrinterOption));
    }

    const submit = (values: ReportDistributionInfo) => {
        const toSave = {
            ...values,
            id: isNew ? null : values.id,
            params: serializeDistributionParams(values.reportId, values.params || {})
        };

        mutateDistribution.mutate(toSave);
    }

    const validate = (values: ReportDistributionInfo) => {
        let err = {};
        Validation.add(err, 'name', Validation.present(values.name));
        Validation.add(err, 'cron', Validation.present(values.cron));
        Validation.add(err, 'cron', validateQuartzCronForMinuteScheduler(values.cron));
        Validation.add(err, 'emails', Validation.present(values.emails));
        if (values.name) Validation.add(err, 'name', values.name.length < 3 ? 'Nazwa dystrybucji za krótka' : null);
        if (hasSelectableValue(values.params && values.params.locationId) && hasSelectableValue(values.params && values.params.printerId)) {
            Validation.add(err, 'params.locationId', 'Wybierz lokację albo urządzenie');
            Validation.add(err, 'params.printerId', 'Wybierz lokację albo urządzenie');
        }
        return err;
    }

    const dist: ReportDistributionInfo = data;
    const initial = useMemo(() => {
        const defaultReportId = reports && reports.length > 0 ? reports[0].id : 'counters_day';
        if (isNew) {
            return {
                name: '',
                cron: DEFAULT_REPORT_CRON,
                reportId: defaultReportId,
                emails: '',
                params: getDefaultParams(defaultReportId)
            };
        }

        if (!dist) {
            return {
                name: '',
                cron: DEFAULT_REPORT_CRON,
                reportId: defaultReportId,
                emails: '',
                params: getDefaultParams(defaultReportId)
            };
        }

        const parsedParams = parseDistributionParams(dist.params);
        return {
            id: data.id,
            name: dist.name,
            reportId: dist.reportId,
            cron: dist.cron,
            emails: dist.emails,
            params: normalizeDistributionParams(dist.reportId, parsedParams, distributionLocation, distributionPrinter),
        };
    }, [data, dist, distributionLocation, distributionPrinter, isNew, reports]);

    if (isLoading) return null;
    if (error) {
        Notifications.pushNew(error.message, 'danger');
        return null;
    }

    if (!reports || reports.length === 0) return null;

    const getFilterSection = (values) => {
        const locationSelected = hasSelectableValue(values.params && values.params.locationId);
        const printerSelected = hasSelectableValue(values.params && values.params.printerId);
        return <div className="distribution-filter-box mt-4">
            <h5 className="mb-1">Filtrowanie</h5>
            <p className="text-muted small mb-3">Opcjonalnie zawęź raport do jednej lokacji albo konkretnego urządzenia.</p>
            <FormRowGroup label="Lokacja" controlId="distributionLocationId">
                <FormikOracleField
                    id="distributionLocationId"
                    name="params.locationId"
                    onSearch={getLocations}
                    disabled={printerSelected}
                    onValueChange={(selected, form) => {
                        if (selected) {
                            form.setFieldValue('params.printerId', null);
                        }
                    }}
                />
            </FormRowGroup>
            <FormRowGroup label="Urządzenie" controlId="distributionPrinterId">
                <FormikOracleField
                    id="distributionPrinterId"
                    name="params.printerId"
                    onSearch={getPrinters}
                    disabled={locationSelected}
                    placeholder="Wyszukaj urządzenie"
                    promptText="Wpisz min. 2 znaki, aby wyszukać urządzenie"
                    emptyLabel="Nie znaleziono urządzeń"
                    onValueChange={(selected, form) => {
                        if (selected) {
                            form.setFieldValue('params.locationId', null);
                        }
                    }}
                />
            </FormRowGroup>
        </div>;
    }

    const getParameters = (reportId) => {
        switch (reportId) {
            case 'counters_range':
            case 'consumables_range': {
                return <>
                    <FormRowGroup label="Ustawienia zakresu" controlId="paramsRange">
                        <FormikControl type="select" name="params.range" options={RangeParamValues}/>
                    </FormRowGroup>
                </>
            }
            case 'counters_day': {
                return <>
                    <FormRowGroup label="Ustawienia zakresu" controlId="paramsDay">
                        <FormikControl type="select" name="params.range" options={DayParamValues}/>
                    </FormRowGroup>
                </>
            }
            default: {
                return <p>Brak parametrów {reportId}</p>
            }
        }
    }


    return <Modal size="lg" show={true} onHide={handleClose} animation={false}>
        <Formik initialValues={initial} onSubmit={submit} validate={validate} enableReinitialize={true}>
            {({ handleSubmit, values}) => (
                <Form noValidate onSubmit={handleSubmit} className="form form-add-printer">
                    <Modal.Header closeButton>
                        <Modal.Title>{isNew ? "Utwórz dystrybucje" : (dist.name + ' - Edycja')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FormRowGroup label="Nazwa" controlId="nameId">
                            <FormikControl autoFocus={true} type="text" name="name" />
                        </FormRowGroup>
                        <FormRowGroup label="Raport" controlId="reportId">
                            <FormikControl type="select" name="reportId" options={
                                reports.map(r=>({value: r.id, label: r.name}))
                            }/>
                        </FormRowGroup>
                        {getParameters(values.reportId)}
                        <FormRowGroup label="Harmonogram" controlId="cronId">
                            <CronEditor name="cron" />
                        </FormRowGroup>
                        <FormRowGroup label="Adresy e-mail (oddzielone przecinkiem)" controlId="emailsId">
                            <FormikControl type="text" name="emails" />
                        </FormRowGroup>
                        {getFilterSection(values)}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleClose}>Anuluj</Button>
                        <Button variant="primary" onClick={handleSubmit}>{isNew ? "Utwórz" : "Aktualizuj"}</Button>
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    </Modal>
}

export default AddEditDistribution;

AddEditDistribution.url = `/distribution${ModalUrl}edit/:id`
AddEditDistribution.buildUrl = location => AddEditDistribution.url.replace(':id', location ? location : 'new')